import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Popover } from "@replo/design-system/components/popover/Popover";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useInsertGoogleFontsUrl } from "@v2/features/design-library/hooks/useInsertGoogleFontsUrl";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { shopMetadataRouter } from "@v2/trpc/routes/shopMetadata";
import { stylesRouter } from "@v2/trpc/routes/styles";
import { uploadToCloudflare } from "@v2/utils/cloudflareUpload";
import {
  MoreHorizontal,
  PaintBucket,
  Pencil,
  ShoppingCart,
  Type,
  Upload,
} from "lucide-react";

import { DesignLibraryColorPresets } from "./DesignLibraryColorPresets";
import { DesignLibraryFontPresets } from "./DesignLibraryFontPresets";
import { DesignLibraryIcon } from "./designLibraryIcon";

interface DesignLibrarySummaryProps {
  styleLibrary: StyleLibrary | null;
  onColorsClick?: () => void;
  onTypographyClick?: () => void;
  onCustomFontsClick?: () => void;
  onStyleLibraryChange?: (styleLibrary: StyleLibrary) => void;
  isPullingStyles?: boolean;
  setIsPullingStyles?: (isPulling: boolean) => void;
}

export const DesignLibrarySummary: React.FC<DesignLibrarySummaryProps> = ({
  styleLibrary,
  onColorsClick,
  onTypographyClick,
  onCustomFontsClick,
  onStyleLibraryChange,
  isPullingStyles = false,
  setIsPullingStyles,
}) => {
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const [activeView, setActiveView] = React.useState<
    "summary" | "colorPresets" | "fontPresets"
  >("summary");
  const [urlInput, setUrlInput] = React.useState("");
  const [isUrlPopoverOpen, setIsUrlPopoverOpen] = React.useState(false);

  const fonts = React.useMemo(() => {
    if (!styleLibrary?.typography) {
      return [];
    }

    return Object.values(styleLibrary.typography).map((font) => ({
      name: font.name,
      weight: font.weight,
    }));
  }, [styleLibrary?.typography]);

  useInsertGoogleFontsUrl(fonts);

  const colors = styleLibrary?.colors;
  const themeColors = {
    light: colors?.neutrals.light ?? "#FFFFFF",
    subtle: colors?.neutrals.subtle ?? "#FFFFFF",
    muted: colors?.neutrals.muted ?? "#FFFFFF",
    dark: colors?.neutrals.dark ?? "#FFFFFF",
    primary: colors?.brand.primary.color ?? "#FFFFFF",
    secondary: colors?.brand.secondary.color ?? "#FFFFFF",
    tertiary: colors?.brand.tertiary.color ?? null,
    accent: colors?.brand.accent.color ?? null,
  };

  const h1Typography = styleLibrary?.typography?.h1;
  const bodyTypography = styleLibrary?.typography?.body;

  const [isLogoUploadLoading, setIsLogoUploadLoading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { mutateAsync: updateProject } = projectRouter.update.useMutation({
    onSuccess: () => {
      void projectUtils.getV2.invalidate();
    },
  });

  const { mutateAsync: generateSignedUrlForLogo } =
    shopMetadataRouter.generateSignedUrlForLogo.useMutation();

  const { mutateAsync: extractStyles, isPending: isExtractingStyles } =
    stylesRouter.extractStyles.useMutation();

  const handleUploadLogo = () => {
    fileInputRef.current?.click();
  };

  const handlePullStyles = async () => {
    if (!projectId || !urlInput.trim()) {
      return;
    }

    setIsPullingStyles?.(true);
    setIsUrlPopoverOpen(false);

    let processedUrl = urlInput;
    if (!urlInput.startsWith("http://") && !urlInput.startsWith("https://")) {
      processedUrl = `https://${urlInput}`;
    }

    try {
      const data = await extractStyles({ url: processedUrl, projectId });

      if (data.styles.styleLibrary) {
        onStyleLibraryChange?.(data.styles.styleLibrary);
      }

      setUrlInput("");
    } finally {
      setIsPullingStyles?.(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (
      !file ||
      (!file.type.startsWith("image/") && file.type !== "image/svg+xml")
    ) {
      return;
    }

    if (!projectId || !project) {
      return;
    }

    try {
      setIsLogoUploadLoading(true);

      const publicUrl = await uploadToCloudflare({
        file,
        filename: file.name,
        projectId,
        fetchSignedUrl: ({ projectId, contentType, contentLength }) => {
          return generateSignedUrlForLogo({
            projectId,
            contentType,
            contentLength,
          });
        },
        errorContext: "logo",
      });

      await updateProject({
        projectId,
        project: {
          logoUrl: publicUrl,
        },
      });
    } finally {
      setIsLogoUploadLoading(false);
    }
  };

  if (activeView === "colorPresets" && styleLibrary) {
    return (
      <DesignLibraryColorPresets
        styleLibrary={styleLibrary}
        onBack={() => setActiveView("summary")}
        onStyleLibraryChange={(updatedLibrary) => {
          onStyleLibraryChange?.(updatedLibrary);
        }}
      />
    );
  }

  if (activeView === "fontPresets" && styleLibrary) {
    return (
      <DesignLibraryFontPresets
        styleLibrary={styleLibrary}
        onBack={() => setActiveView("summary")}
        onStyleLibraryChange={(updatedLibrary) => {
          onStyleLibraryChange?.(updatedLibrary);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-xl border-0.5 border-border bg-white shadow-sm">
      <div className="flex justify-between items-center py-6 md:px-4 self-stretch border-b-0.5 border-border">
        <div className="header-h5">Your Library</div>
        <div className="flex gap-2">
          <Popover
            open={isUrlPopoverOpen}
            onOpenChange={(open) => {
              if (isPullingStyles) {
                return;
              }
              setIsUrlPopoverOpen(open);
            }}
            title="Pull styles from URL"
            description="You can save these styles as your new design library."
            side="bottom"
            align="end"
            trigger={
              <Tooltip content="Pull styles from URL" triggerAsChild>
                <Button
                  variant="secondary"
                  size="sm"
                  icon
                  disabled={isPullingStyles}
                >
                  <PaintBucket />
                </Button>
              </Tooltip>
            }
            footer={
              <>
                <Button
                  variant="secondary"
                  size="base"
                  onClick={() => setIsUrlPopoverOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="base"
                  onClick={() => void handlePullStyles()}
                  disabled={!urlInput.trim() || isExtractingStyles}
                  loading={isExtractingStyles}
                >
                  Pull Styles
                </Button>
              </>
            }
          >
            <Input
              type="text"
              placeholder="example.com"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  urlInput.trim() &&
                  !isExtractingStyles
                ) {
                  void handlePullStyles();
                }
              }}
              size="base"
              autoFocus
            />
          </Popover>
          <Menu>
            <MenuTrigger
              render={
                <Button
                  variant="secondary"
                  size="sm"
                  icon
                  disabled={isPullingStyles}
                >
                  <MoreHorizontal />
                </Button>
              }
            />
            <MenuContent align="end" side="bottom">
              <MenuItem
                label="Manage custom fonts"
                startEnhancer={<Type />}
                onClick={() => {
                  onCustomFontsClick?.();
                }}
              />
              <MenuItem
                label="View color presets"
                startEnhancer={<DesignLibraryIcon />}
                onClick={() => {
                  setActiveView("colorPresets");
                }}
              />
              <MenuItem
                label="View text presets"
                startEnhancer={<DesignLibraryIcon />}
                onClick={() => {
                  setActiveView("fontPresets");
                }}
              />
            </MenuContent>
          </Menu>
        </div>
      </div>
      <div className="flex flex-col flex-1 self-stretch overflow-y-auto no-scrollbar">
        <div
          className={twMerge(
            "flex flex-1 py-6 md:px-4 flex-col items-start gap-6 self-stretch border-b-0.5 border-border cursor-pointer hover:bg-background transition-colors",
            isPullingStyles && "cursor-default",
          )}
          onClick={isPullingStyles ? undefined : onColorsClick}
        >
          <div className="flex justify-between items-start self-stretch">
            <div className="header-h6">Colors</div>
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onColorsClick?.();
              }}
              disabled={isPullingStyles}
            >
              Edit
            </Button>
          </div>
          {isPullingStyles ? (
            <Skeleton className="w-full h-16 rounded-xl" />
          ) : (
            themeColors && (
              <div className="flex items-center self-stretch">
                {Object.entries(themeColors)
                  .filter(([, color]) => Boolean(color))
                  .map(([key, color], index) => (
                    <div
                      key={key}
                      className={twMerge(
                        "h-16 rounded-lg border-0.5 border-black/10",
                        index > 0 ? "-ml-4" : "",
                      )}
                      style={{
                        backgroundColor: color ?? "",
                        flex: "1 0 0",
                        alignSelf: "stretch",
                      }}
                    ></div>
                  ))}
              </div>
            )
          )}
        </div>
        <div
          className={twMerge(
            "flex flex-2 py-6 md:px-4 flex-col items-start gap-6 self-stretch border-b-0.5 border-border cursor-pointer hover:bg-background transition-colors",
            isPullingStyles && "cursor-default",
          )}
          onClick={isPullingStyles ? undefined : onTypographyClick}
        >
          <div className="flex justify-between items-start self-stretch">
            <div className="header-h6">Typography</div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onCustomFontsClick?.();
                }}
                disabled={isPullingStyles}
              >
                Custom Fonts
              </Button>
              <Button
                variant="secondary"
                size="sm"
                startEnhancer={<Pencil />}
                onClick={(e) => {
                  e.stopPropagation();
                  onTypographyClick?.();
                }}
                disabled={isPullingStyles}
              >
                Edit
              </Button>
            </div>
          </div>
          {isPullingStyles ? (
            <>
              <div
                className="flex flex-col p-3 justify-center items-center self-stretch rounded-lg gap-1"
                style={{
                  background:
                    "linear-gradient(277deg, rgba(253, 211, 126, 0.05) -11.74%, rgba(243, 77, 114, 0.05) 19.68%, rgba(124, 127, 186, 0.05) 109.58%)",
                }}
              >
                <Skeleton className="h-12 w-60 rounded-xl" />
                <Skeleton className="h-4 w-100 rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3 self-stretch">
                <div className="flex p-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
                  <div className="text-xs font-medium text-muted">Headings</div>
                  <Skeleton className="h-9 w-full rounded-xl" />
                </div>
                <div className="flex p-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
                  <div className="text-xs font-medium text-muted">Body</div>
                  <Skeleton className="h-9 w-full rounded-xl" />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col p-3 justify-center items-center self-stretch rounded-lg bg-sheet border-0.5 border-black/10 gap-1">
                <div
                  className="flex h-8 flex-col justify-center items-center self-stretch text-center"
                  style={{
                    fontFamily: h1Typography?.name
                      ? `'${h1Typography.name}', sans-serif`
                      : undefined,
                    fontSize: "42px",
                    fontWeight: h1Typography?.weight ?? 600,
                  }}
                >
                  Heading
                </div>
                <div
                  style={{
                    fontFamily: bodyTypography?.name
                      ? `'${bodyTypography.name}', sans-serif`
                      : undefined,
                    fontSize: "16px",
                    fontWeight: bodyTypography?.weight ?? 400,
                  }}
                >
                  This is your paragraph.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 self-stretch">
                <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
                  <div className="text-xs font-medium text-muted">Headings</div>
                  <div
                    className="text-2xl font-semibold truncate min-w-0 w-full"
                    style={{
                      fontFamily: h1Typography?.name
                        ? `'${h1Typography.name}', sans-serif`
                        : undefined,
                    }}
                    title={h1Typography?.name ?? "Default"}
                  >
                    {h1Typography?.name ?? "Default"}
                  </div>
                </div>
                <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
                  <div className="text-xs font-medium text-muted">Body</div>
                  <div
                    className="text-2xl font-semibold truncate min-w-0 w-full"
                    style={{
                      fontFamily: bodyTypography?.name
                        ? `'${bodyTypography.name}', sans-serif`
                        : undefined,
                    }}
                    title={bodyTypography?.name ?? "Default"}
                  >
                    {bodyTypography?.name ?? "Default"}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex flex-1 py-6 md:px-4 flex-col items-start gap-6 self-stretch hover:bg-background transition-colors">
          <div className="flex justify-between items-start self-stretch">
            <div className="header-h6">Site Logo</div>
            <Button
              variant="secondary"
              size="sm"
              startEnhancer={<Upload />}
              onClick={handleUploadLogo}
              loading={isLogoUploadLoading}
              disabled={isLogoUploadLoading || isPullingStyles}
            >
              {project?.logoUrl ? "Replace" : "Upload"}
            </Button>
          </div>
          <div className="text-xs text-muted -mt-5">
            Upload a PNG, JPG, or SVG logo for your site header.
          </div>
          {isPullingStyles ? (
            <div className="flex items-center justify-center self-stretch p-4 pr-0 rounded-lg bg-sheet border-0.5 border-black/10 min-h-24">
              <div className="flex items-center justify-between p-4 bg-white w-full h-full rounded-l-lg">
                <Skeleton className="h-8 w-35 rounded-xl" />
                <Button
                  variant="secondary"
                  size="base"
                  icon
                  UNSAFE_className="pointer-events-none cursor-default"
                  disabled
                >
                  <ShoppingCart />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center self-stretch p-4 pr-0 rounded-lg bg-sheet border-0.5 border-black/10 min-h-24">
              <div className="flex items-center justify-between p-4 bg-white w-full h-full rounded-l-lg">
                <div className="flex items-center">
                  <img
                    src={project?.logoUrl ?? "/your-logo.svg"}
                    alt="Site Logo"
                    className="max-h-12 max-w-24 object-contain"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="base"
                  icon
                  UNSAFE_className="pointer-events-none cursor-default"
                >
                  <ShoppingCart />
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={(e) => void handleFileChange(e)}
                className="hidden"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
